import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { salesDb } from "@/integrations/supabase/sales-client";
import type { StudentKind } from "./useEventBalances";

export type StudentSearchResult = {
  code: string;
  kind: StudentKind;
  first_name: string | null;
  last_name: string | null;
  nickname: string | null;
  event_balance: number;
};

export const useEventStudentSearch = (search: string) => {
  return useQuery({
    enabled: search.trim().length >= 1,
    queryKey: ["event-student-search", search],
    queryFn: async () => {
      const s = `%${search.trim()}%`;
      const term = search.trim();
      const [fsRes, escRes, knRes] = await Promise.all([
        (supabase as any)
          .schema("student")
          .from("fishsix_students")
          .select("code,first_name,last_name,nickname")
          .eq("is_active", true)
          .is("deleted_at", null)
          .or(`code.ilike.${s},first_name.ilike.${s},last_name.ilike.${s},nickname.ilike.${s}`)
          .limit(20),
        supabase.rpc("get_esc_students", { search_term: term }),
        salesDb()
          .from("knoware_students_view")
          .select("code,student_name_th,student_nickname_th")
          .or(`code.ilike.${s},student_name_th.ilike.${s},student_nickname_th.ilike.${s}`)
          .limit(20),
      ]);
      const fsRows = (fsRes.data ?? []) as any[];
      const escRows = (escRes.data ?? []) as any[];
      const knRows = (knRes.data ?? []) as any[];
      const fsCodes = fsRows.map((r) => r.code);
      const escCodes = escRows.map((r) => r.code);
      const knCodes = knRows.map((r) => r.code).filter(Boolean);

      const [fsBalRes, escBalRes, knBalRes] = await Promise.all([
        fsCodes.length
          ? salesDb()
              .from("v_student_event_balance")
              .select("fishsix_student_code,event_balance")
              .in("fishsix_student_code", fsCodes)
          : Promise.resolve({ data: [] } as any),
        escCodes.length
          ? salesDb()
              .from("v_student_event_balance")
              .select("esc_student_code,event_balance")
              .in("esc_student_code", escCodes)
          : Promise.resolve({ data: [] } as any),
        knCodes.length
          ? salesDb()
              .from("v_student_event_balance")
              .select("knoware_student_code,event_balance")
              .in("knoware_student_code", knCodes)
          : Promise.resolve({ data: [] } as any),
      ]);
      const fsBm = new Map<string, number>();
      (fsBalRes.data ?? []).forEach((b: any) =>
        fsBm.set(b.fishsix_student_code, Number(b.event_balance ?? 0))
      );
      const escBm = new Map<string, number>();
      (escBalRes.data ?? []).forEach((b: any) =>
        escBm.set(b.esc_student_code, Number(b.event_balance ?? 0))
      );
      const knBm = new Map<string, number>();
      (knBalRes.data ?? []).forEach((b: any) =>
        knBm.set(b.knoware_student_code, Number(b.event_balance ?? 0))
      );

      const out: StudentSearchResult[] = [
        ...fsRows.map((r) => ({
          code: r.code,
          kind: "fs" as const,
          first_name: r.first_name,
          last_name: r.last_name,
          nickname: r.nickname,
          event_balance: fsBm.get(r.code) ?? 0,
        })),
        ...escRows.map((r: any) => ({
          code: r.code,
          kind: "esc" as const,
          first_name: r.name ?? null,
          last_name: null,
          nickname: r.nickname ?? null,
          event_balance: escBm.get(r.code) ?? 0,
        })),
        ...knRows.map((r: any) => ({
          code: r.code,
          kind: "kn" as const,
          first_name: r.student_name_th ?? null,
          last_name: null,
          nickname: r.student_nickname_th ?? null,
          event_balance: knBm.get(r.code) ?? 0,
        })),
      ];
      return out;
    },
  });
};
